import { useEffect, useState } from "react";
import { X, User, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectSiteHeaderColor,
  selectSiteLoginColor,
  selectSiteSignUpColor,
  selectSiteLogo,
  selectDomainID,
  selectSiteName,
  selectSiteFevicon,
  selectSite,
} from "./../../../AsianExchangeSrc/utils/helper/commonSelectors";
import { generatePresignedUrl } from "../../../AsianExchangeSrc/utils/helper/generatePresignedUrl";
import { getSitesByDomain, SendOtp, signUp } from "../../../AsianExchangeSrc/api";

export default function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const domainId = useSelector(selectDomainID());
  const headerColor = useSelector(selectSiteHeaderColor());
  const loginBtnColor = useSelector(selectSiteLoginColor());
  const signUpBtnColor = useSelector(selectSiteSignUpColor());
  const siteName = useSelector(selectSiteName());
  const siteLogo = useSelector(selectSiteLogo());
  const siteFavIcon = useSelector(selectSiteFevicon());
  const [logoUrl, setLogoUrl] = useState(null);
  const [favIconUrl, setFavIconUrl] = useState(null);

  const [otpSent, setOtpSent] = useState(false);
  const [params, setParams] = useState({});
  const [siteData, setUserData] = useState(null);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [formData, setFormData] = useState({
    mobile: "",
    userName: "",
    name: "",
    password: "",
    confirmPassword: "",
    x_panel_type: "user",
    commission: 0,
    openingBalance: 0,
    availableBalance: 0,
    creditReference: 0,
    partnership: 0,
    currency: "INR",
    exposureLimit: 0,
    exposure: 0,
    createdBy: "",
    deviceIp: "0.0.0",
    role: "",
    domain: "",
    ownerDomainId: "",
    otp: "",
    isSignupUser: true,
    layout: "auraexchange",
  });

   useEffect(() => {
      const fetchSiteDetails = async () => {
        try {
          const domainName = window.location.host;
          const response = await getSitesByDomain(domainName);
          const siteDetails = response?.data;
          setUserData(siteDetails);
        } catch (error) {
          console.error("Failed to fetch site details:", error);
        }
      };
      fetchSiteDetails();
    }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setParams({ reffrel: queryParams.get("reffrel") });
  }, []);

  useEffect(() => {
    if (siteData) {
      setFormData((prev) => ({
        ...prev,
        createdBy: params.reffrel
          ? ""
          : siteData?.createdBy?._id || siteData?.siteDetails?.createrId || "",
        role: siteData?.userRoleId || "",
        domain: siteData?.siteDetails?.domainDetails?._id || "",
        ownerDomainId: siteData?.siteDetails?.admindomainName?._id || "",
        ...(params.reffrel && { referralKey: params.reffrel }),
      }));
    }
  }, [siteData, params.reffrel]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "password") {
      validatePassword(value);
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      mobile: value,
      userName: value,
      name: value,
    }));
    setErrors((prev) => ({ ...prev, mobile: "" }));
  };

  const validatePassword = (password) => {
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@]{6,}$/;
    const hasInvalidSpecialChar = /[^A-Za-z\d@]/.test(password);

    if (!strongPasswordRegex.test(password) || hasInvalidSpecialChar) {
      setPasswordError(
        "Use 6+ chars: uppercase, lowercase, number, '@' optional."
      );
    } else {
      setPasswordError("");
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setFormData((prev) => ({
      ...prev,
      otp: value,
    }));
    setErrors((prev) => ({ ...prev, otp: "" }));
  };

  const handleGetOTP = async () => {
    setIsOtpLoading(true);
    try {
      const response = await SendOtp({
        mobile: formData.mobile,
      });
      if (response?.success) {
        toast.success(response?.data?.message || "OTP sent successfully");
        setOtpSent(true);
      } else {
        toast.error(response?.data?.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error sending OTP. Please try again."
      );
    } finally {
      setIsOtpLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (formData.mobile.length !== 10)
      newErrors.mobile = "Mobile number must be 10 digits.";
    if (!formData.name) newErrors.name = "Name is required.";
    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match.";
    if ((otpSent && formData.otp.length !== 4)&& formData.otp)
      newErrors.otp = "OTP must be 4 digits.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors.");
      return;
    }

    if (passwordError) {
      toast.error("Please fix password requirements.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        userName: formData.userName.trim(),
      };

      const response = await signUp(payload);
      if (response?.success) {
        toast.success("Registration Successful!");
        navigate("/", { replace: true });
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Signup Error:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const isMobileValid = formData.mobile && /^\d{10}$/.test(formData.mobile);
  const isFormValid = () =>
    isMobileValid &&
    formData.name &&
    formData.password &&
    formData.confirmPassword === formData.password &&
    !passwordError &&
    (!otpSent || (otpSent && formData.otp.length === 4));

  useEffect(() => {
    if (!siteLogo && !siteFavIcon && !siteName) return;

    const fetchAssets = async () => {
      try {
        const [logoPresigned, favIconPresigned] = await Promise.all([
          siteLogo ? generatePresignedUrl(siteLogo) : Promise.resolve(null),
          siteFavIcon
            ? generatePresignedUrl(siteFavIcon)
            : Promise.resolve(null),
        ]);

        if (logoPresigned) setLogoUrl(logoPresigned);
        if (favIconPresigned) setFavIconUrl(favIconPresigned);
        if (siteName) document.title = siteName;
      } catch (error) {
        console.error("Error fetching site assets:", error);
        toast.error("Failed to load site assets. Please try again.");
      }
    };

    fetchAssets();
  }, [siteLogo, siteFavIcon, siteName]);

  return (
    <div
      className="h-screen overflow-hidden bg-gradient-to-b flex flex-col items-center justify-center p-4 px-10 sm:px-0 relative"
      style={{
        backgroundImage: `linear-gradient(180deg, ${
          headerColor?.start || "#000"
        }, ${headerColor?.end || "#000"})`,
      }}
    >
      <button
        className="absolute top-6 right-6 text-black p-1 text-bold rounded-md hover:text-gray-700 transition-colors"
        onClick={() => navigate("/")}
        style={{
          backgroundImage: `linear-gradient(180deg, ${
            headerColor?.start || "#000"
          }, ${headerColor?.end || "#000"})`,
        }}
      >
        <X size={24} />
      </button>

      <div className="w-full max-w-sm space-y-8">
        <div className="flex justify-center">
          {logoUrl && <img src={logoUrl} alt={siteName} className="h-14" />}
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="relative">
            <input
              type="tel"
              name="mobile"
              placeholder="Mobile number"
              value={formData.mobile}
              onChange={handleMobileChange}
              className={`w-full px-4 py-3 pr-12 bg-white rounded-lg border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                errors.mobile ? "border-red-500" : ""
              }`}
            />
            <button
              type="button"
              className="px-4 py-2 absolute right-2 text-xs top-2 rounded-lg border-0 text-white placeholder-gray-500 focus:outline-none focus:ring-2"
              onClick={handleGetOTP}
              disabled={!isMobileValid || isOtpLoading}
              style={{
                background: `linear-gradient(180deg, ${
                  loginBtnColor?.start || "#045662"
                }, ${loginBtnColor?.end || "#045662"})`,
              }}
            >
              {isOtpLoading ? (
                <span className='font-bold'>...</span>
              ) : (
                <span className="font-bold">Get OTP</span>
              )}
            </button>
            {errors.mobile && (
              <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
            )}
          </div>

          {otpSent && (
            <div className="mb-3">
              <input
                type="number"
                id="otp"
                name="otp"
                className={`w-full px-4 py-3 pr-12 bg-white rounded-lg border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  errors.otp ? "border-red-500" : ""
                }`}
                placeholder="Enter OTP"
                value={formData.otp}
                onChange={handleOtpChange}
              />
              {errors.otp && (
                <p className="text-red-500 text-xs mt-1">{errors.otp}</p>
              )}
            </div>
          )}

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pr-12 bg-white rounded-lg border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                errors.password ? "border-red-500" : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {showPassword ? (
                <EyeOff size={20} fill="black" />
              ) : (
                <Eye size={20} />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-xs mt-1">{errors.password}</p>
          )}
          {passwordError && (
            <p className="text-red-500 text-xs mt-1">{passwordError}</p>
          )}

          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pr-12 bg-white rounded-lg border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                errors.confirmPassword ? "border-red-500" : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {showConfirmPassword ? (
                <EyeOff size={20} fill="black" />
              ) : (
                <Eye size={20} />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
          )}

          <div className="space-y-1.5 pt-4">
            <button
              type="submit"
              className="w-full py-3 text-white font-semibold rounded-lg transition-colors duration-200 flex justify-center items-center space-x-3"
              disabled={isLoading}
              style={{
                background: `linear-gradient(180deg, ${
                  loginBtnColor?.start || "#045662"
                }, ${loginBtnColor?.end || "#045662"})`,
              }}
            >
              {isLoading ? (
                <span>Signing up...</span>
              ) : (
                <span>Sign Up</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}